import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, DestroyRef, inject, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../user.service';
import { EmailInput } from 'app/shared/email-input/email-input';
import { SiteSpecific, Site, UserSession } from '../../core/site-specific.base';
import { LoggerFactory } from '../../core/logger.service';
import { UserRole } from './invite-user';
import { IMtr } from 'app/core/tracker/mtr.interface';
import { IAmplitude } from 'app/core/tracker/amplitude.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { AccountService } from 'app/account/account.service';
import { BasicAccount } from 'app/back-office/account.model';
import { catchError, of } from 'rxjs';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';

@Component({
  selector: 'app-invite-user',
  templateUrl: './invite-user.component.html',
  styleUrls: ['./invite-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InviteUserComponent extends SiteSpecific implements OnInit {
  private readonly accountService = inject(AccountService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(MatSelectionList) private selectionList: MatSelectionList;

  public userEmail: string;
  private isValid: boolean;
  private userRoleLimited: UserRole = UserRole.Limited;
  private userRoleAccountAdmin: UserRole = UserRole.AccountAdmin;
  private currentSite: Site;
  public currentSiteName = '';

  public roles: UserRole[] = [this.userRoleAccountAdmin, this.userRoleLimited];
  public selectedRole: UserRole = this.userRoleAccountAdmin;
  public submitting: boolean;

  public otherAccounts: BasicAccount[] = [];
  public allAccountsSelected = false;
  public someAccountsSelected = false;
  private selectedExtraSiteIds = new Set<number>();

  constructor(
    readonly userSession: UserSession,
    readonly loggerFactory: LoggerFactory,
    public dialogRef: MatDialogRef<InviteUserComponent>,
    private readonly snackBarService: SnackBarService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly userService: UserService,
    @Inject('Mtr') private readonly mtr: IMtr,
    @Inject('Amplitude') private readonly amplitude: IAmplitude,
  ) {
    super(userSession, loggerFactory);
  }

  // If you override ngOnInit, super.ngOnInit() must be called (see SiteSpecific base)
  async ngOnInit() {
    await super.ngOnInit();
    this.loadOtherAccounts();
  }

  protected async onSiteChanged(site: Site) {
    this.currentSite = site;
    this.currentSiteName = site?.name ?? '';
  }

  private loadOtherAccounts(): void {
    this.accountService
      .getAllAccounts()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of([])),
      )
      .subscribe((accounts) => {
        this.otherAccounts = accounts.filter((a) => a.siteId !== this.currentSite?.id);
      });
  }

  public async submitAsync() {
    if (!this.isValid) {
      return;
    }

    this.submitting = true;
    try {
      const inviteResult = await this.userService.inviteUser(
        this.userEmail,
        null,
        null,
        this.selectedRole,
        this.currentSite.name,
        [...this.selectedExtraSiteIds],
      );

      if (inviteResult) {
        this.snackBarService.success('generic.success');
        this.mtr.goal('Invited team member');
        this.amplitude.logEvent('Invited team member');
        this.close();
      } else {
        this.snackBarService.error('generic.error');
      }
    } finally {
      this.submitting = false;
    }
  }

  public close() {
    this.dialogRef.close();
  }

  public async emailChanged(input: EmailInput) {
    this.userEmail = input.emailAddress;
    this.isValid = input.isValid;
  }

  public convertToString(userRoleNumber: number): string {
    return UserRole[userRoleNumber];
  }

  public onAccountSelectionChange(event: MatSelectionListChange): void {
    event.options.forEach((option) => {
      if (option.disabled) return;
      const siteId = option.value as number;
      if (option.selected) {
        this.selectedExtraSiteIds.add(siteId);
      } else {
        this.selectedExtraSiteIds.delete(siteId);
      }
    });
    this.updateSelectionState();
  }

  public toggleSelectAll(checked: boolean): void {
    this.selectionList?.options
      .filter((o) => !o.disabled)
      .forEach((o) => {
        o.selected = checked;
      });
    if (checked) {
      this.otherAccounts.forEach((a) => this.selectedExtraSiteIds.add(a.siteId));
    } else {
      this.selectedExtraSiteIds.clear();
    }
    this.updateSelectionState();
  }

  private updateSelectionState(): void {
    const total = this.otherAccounts.length;
    const count = this.selectedExtraSiteIds.size;
    this.allAccountsSelected = total > 0 && count === total;
    this.someAccountsSelected = count > 0 && count < total;
  }
}
